import { useState, useEffect, useMemo } from 'react';
import { DashboardLayout } from '@/components/ui/dashboard-layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useActivity } from '@/hooks/use-activity';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { useAuthStore } from '@/stores/auth-store';
import { SkeletonRow } from '@/components/ui/skeleton';
import {
  Activity,
  History,
  UserPlus,
  FileText,
  ArrowRight,
  CheckCircle2,
  UserCheck,
  Receipt,
  Ticket,
  Clock,
  Filter,
  Loader2,
  ChevronDown,
  ChevronUp,
  Download,
  Globe,
  Mail,
  Settings,
  CreditCard,
} from 'lucide-react';
import { cn, formatDistanceToNow } from '@/lib/utils';

const PAGE_SIZE = 20;

/** Date helper: format a date as a section header */
function formatDateSection(date: Date): string {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const entryDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((today.getTime() - entryDate.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

const actionIcons: Record<string, React.ElementType> = {
  'auth.login': UserPlus,
  'auth.password_reset': History,
  'auth.password_change': CheckCircle2,
  'project.create': FileText,
  'project.update': ArrowRight,
  'project.delete': History,
  'task.create': FileText,
  'task.update': ArrowRight,
  'task.delete': History,
  'invoice.create': Receipt,
  'invoice.paid': CheckCircle2,
  'invoice.payment_initiated': Receipt,
  'ticket.create': Ticket,
  'ticket.update': ArrowRight,
  'user.create': UserCheck,
  'user.update': UserCheck,
  'file.upload': FileText,
};

const actionColors: Record<string, string> = {
  'auth.login': 'bg-blue-500/10 text-blue-400',
  'auth.password_reset': 'bg-yellow-500/10 text-yellow-400',
  'auth.password_change': 'bg-green-500/10 text-green-400',
  'project.create': 'bg-purple-500/10 text-purple-400',
  'project.update': 'bg-indigo-500/10 text-indigo-400',
  'project.delete': 'bg-red-500/10 text-red-400',
  'task.create': 'bg-cyan-500/10 text-cyan-400',
  'task.update': 'bg-blue-500/10 text-blue-400',
  'task.delete': 'bg-red-500/10 text-red-400',
  'invoice.create': 'bg-emerald-500/10 text-emerald-400',
  'invoice.paid': 'bg-green-500/10 text-green-400',
  'ticket.create': 'bg-orange-500/10 text-orange-400',
  'ticket.update': 'bg-yellow-500/10 text-yellow-400',
};

const actionLabels: Record<string, string> = {
  'auth.login': 'logged in',
  'auth.password_reset': 'reset their password',
  'auth.password_change': 'changed their password',
  'project.create': 'created a project',
  'project.update': 'updated a project',
  'project.delete': 'deleted a project',
  'task.create': 'created a task',
  'task.update': 'updated a task',
  'task.delete': 'deleted a task',
  'invoice.create': 'created an invoice',
  'invoice.paid': 'marked an invoice as paid',
  'invoice.payment_initiated': 'initiated payment for an invoice',
  'ticket.create': 'created a ticket',
  'ticket.update': 'updated a ticket',
  'user.create': 'created a user',
  'user.update': 'updated a user',
  'file.upload': 'uploaded a file',
};

/** Friendly summary of metadata based on action type */
function formatMetadata(action: string, metadata: Record<string, unknown> | null): string | null {
  if (!metadata) return null;

  // For entity-update actions, show what changed
  const changes = 'changes' in metadata ? metadata.changes : undefined;
  if (action.endsWith('.update') && changes && typeof changes === 'object') {
    const keys = Object.keys(changes as Record<string, unknown>);
    if (keys.length === 0) return null;
    return `Changed ${keys.join(', ')}`;
  }

  // For task/project creation, show the name
  if (typeof metadata.name === 'string') return `"${metadata.name.slice(0, 100)}"`;
  if (typeof metadata.title === 'string') return `"${metadata.title.slice(0, 100)}"`;

  // For invoices, show amount
  if (typeof metadata.amount === 'number') return `Amount: £${metadata.amount.toFixed(2)}`;
  if (typeof metadata.total === 'number') return `Total: £${metadata.total.toFixed(2)}`;

  // Fallback: show a summary of top-level keys
  const parts = Object.entries(metadata).slice(0, 3).map(([k, v]) => {
    const val = typeof v === 'object' ? JSON.stringify(v).slice(0, 60) : String(v).slice(0, 60);
    return `${k}: ${val}`;
  });
  return parts.join(' | ');
}

export function ActivityPage() {
  useDocumentTitle('Activity');
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';

  const [entityFilter, setEntityFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [actionGroupFilter, setActionGroupFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [expandedMetadata, setExpandedMetadata] = useState<Set<string>>(new Set());

  // Accumulate activity across pages so "Load More" appends, not replaces
  const [accumulatedActivity, setAccumulatedActivity] = useState<any[]>([]);

  const params: Record<string, string> = { page: String(page), limit: String(PAGE_SIZE) };
  if (entityFilter) params.entityType = entityFilter;
  if (actionFilter) params.action = actionFilter;
  if (actionGroupFilter) params.actionGroup = actionGroupFilter;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;

  const { data: activityData, isLoading, isFetching } = useActivity(params);
  const rawActivity: any[] = activityData?.data || [];
  const availableActionGroups: string[] = (activityData as { availableActionGroups?: string[] })?.availableActionGroups || [];
  const paginationInfo = activityData && 'pagination' in activityData
    ? (activityData as { pagination: { totalPages: number; total: number } }).pagination
    : undefined;
  const totalPages = paginationInfo?.totalPages || 1;
  const totalEntries = paginationInfo?.total || 0;
  const hasMore = page < totalPages;

  const toggleMetadata = (id: string) => {
    setExpandedMetadata((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /** Group activity by date for section headers */
  const groupedActivity = useMemo(() => {
    const activity = accumulatedActivity.length > 0 ? accumulatedActivity : rawActivity;
    const groups: { label: string; entries: any[] }[] = [];
    const map = new Map<string, any[]>();

    activity.forEach((entry: any) => {
      const label = formatDateSection(new Date(entry.createdAt));
      if (!map.has(label)) map.set(label, []);
      map.get(label)!.push(entry);
    });

    map.forEach((entries, label) => {
      groups.push({ label, entries });
    });

    return groups;
  }, [accumulatedActivity, rawActivity]);

  /** Export filtered audit log as CSV */
  const handleExport = () => {
    const allEntries = accumulatedActivity.length > 0 ? accumulatedActivity : rawActivity;
    if (allEntries.length === 0) return;

    const headers = ['Timestamp', 'User', 'Role', 'Action', 'Entity Type', 'Entity ID', 'IP Address', 'Metadata'];
    const rows = allEntries.map((e: any) => [
      new Date(e.createdAt).toISOString(),
      e.user?.name || 'System',
      e.user?.role || '',
      e.action,
      e.entityType,
      e.entityId || '',
      e.ipAddress || '',
      e.metadata ? JSON.stringify(e.metadata) : '',
    ]);

    const csv = [headers.join(','), ...rows.map((r: string[]) => r.map((c: string) => `"${c.replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Accumulate data across pages (preserve existing entries when loading next page)
  useEffect(() => {
    if (rawActivity.length === 0) return;
    setAccumulatedActivity((prev) => {
      if (page === 1) return rawActivity;
      const existingIds = new Set(prev.map((e: any) => e.id));
      const newEntries = rawActivity.filter((e: any) => !existingIds.has(e.id));
      return [...prev, ...newEntries];
    });
  }, [rawActivity, page]);

  // Reset accumulated data when filters change (page will be 1)
  useEffect(() => {
    setAccumulatedActivity([]);
  }, [entityFilter, actionFilter, actionGroupFilter, dateFrom, dateTo]);

  const isFirstLoad = isLoading && page === 1;
  const activity = accumulatedActivity.length > 0 ? accumulatedActivity : rawActivity;

  function getActionIcon(action: string): React.ElementType {
    return actionIcons[action] || History;
  }

  function getActionColor(action: string): string {
    return actionColors[action] || 'bg-gray-500/10 text-gray-400';
  }

  function getActionLabel(action: string): string {
    return actionLabels[action] || action?.replace(/\./g, ' ') || 'performed an action';
  }

  function getEntityLabel(entityType: string): string {
    return entityType?.replace(/_/g, ' ') || 'unknown';
  }

  const handleLoadMore = () => {
    setPage((p) => p + 1);
  };

  const handleEntityFilterChange = (v: string) => {
    setEntityFilter(v === 'all' ? '' : v);
    setPage(1);
  };

  const handleActionFilterChange = (v: string) => {
    setActionFilter(v === 'all' ? '' : v);
    setPage(1);
  };

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity size={24} className="text-dyndev-green" />
            {isAdmin ? 'Audit Log' : 'Activity Feed'}
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            {isAdmin
              ? 'Track all changes, actions, and system events across the platform.'
              : 'Track all changes and actions across the system.'}
          </p>
        </div>
        {isAdmin && activity.length > 0 && !isFirstLoad && (
          <Button variant="outline" size="sm" onClick={handleExport} className="gap-1.5 flex-shrink-0">
            <Download size={14} />
            Export CSV
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 mb-6">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 flex-wrap">
          <Filter size={14} className="text-[var(--text-secondary)] flex-shrink-0 hidden sm:block" />

          <Select value={entityFilter || 'all'} onValueChange={handleEntityFilterChange}>
            <SelectTrigger className="w-full sm:w-[150px]" aria-label="Filter by entity type">
              <SelectValue placeholder="All entities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All entities</SelectItem>
              <SelectItem value="user">Users</SelectItem>
              <SelectItem value="project">Projects</SelectItem>
              <SelectItem value="task">Tasks</SelectItem>
              <SelectItem value="invoice">Invoices</SelectItem>
              <SelectItem value="ticket">Tickets</SelectItem>
            </SelectContent>
          </Select>

          <Select value={actionFilter || 'all'} onValueChange={handleActionFilterChange}>
            <SelectTrigger className="w-full sm:w-[150px]" aria-label="Filter by action type">
              <SelectValue placeholder="All actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              <SelectItem value="create">Create</SelectItem>
              <SelectItem value="update">Update</SelectItem>
              <SelectItem value="delete">Delete</SelectItem>
              <SelectItem value="login">Login</SelectItem>
              <SelectItem value="paid">Payment</SelectItem>
            </SelectContent>
          </Select>

          {/* Action group filter (admin only) */}
          {isAdmin && availableActionGroups.length > 0 && (
            <Select value={actionGroupFilter || 'all'} onValueChange={(v) => {
              setActionGroupFilter(v === 'all' ? '' : v);
              setPage(1);
            }}>
              <SelectTrigger className="w-full sm:w-[180px]" aria-label="Filter by action group">
                <SelectValue placeholder="All groups" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All groups</SelectItem>
                {availableActionGroups.map((group) => (
                  <SelectItem key={group} value={group}>{group}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Date range filter (admin only) */}
        {isAdmin && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-[var(--text-secondary)] flex-shrink-0">Date range:</span>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="w-[140px] h-8 text-xs"
              aria-label="From date"
            />
            <span className="text-xs text-[var(--text-secondary)]">to</span>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="w-[140px] h-8 text-xs"
              aria-label="To date"
            />
            {(dateFrom || dateTo) && (
              <button
                onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); }}
                className="text-[10px] text-dyndev-green hover:underline"
              >
                Clear
              </button>
            )}
          </div>
        )}

        {activity.length > 0 && !isFirstLoad && (
          <p className="text-[10px] text-[var(--text-secondary)] sm:text-right">
            {activity.length} {activity.length === 1 ? 'entry' : 'entries'}
            {totalEntries > activity.length && ` of ${totalEntries} total`}
          </p>
        )}
      </div>

      {/* Activity list */}
      {isFirstLoad ? (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y divide-[var(--border-color)]">
              {Array.from({ length: 8 }).map((_, i) => (
                <SkeletonRow key={i} />
              ))}
            </div>
          </CardContent>
        </Card>
      ) : activity.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Activity size={48} className="text-[var(--text-secondary)] mb-4" />
            <h3 className="text-lg font-semibold mb-1">No activity yet</h3>
            <p className="text-sm text-[var(--text-secondary)]">
              {entityFilter || actionFilter || actionGroupFilter || dateFrom || dateTo
                ? 'No activity matches your filters. Try adjusting them.'
                : 'System activity will appear here as changes are made.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="relative">
          {/* Timeline line — centred on the dot column (16px padding + 23px half-dot = 39px) */}
          <div className="absolute left-[39px] top-0 bottom-0 w-px bg-[var(--border-color)]" aria-hidden="true" />

          <div className="space-y-6">
            {groupedActivity.map((group) => (
              <div key={group.label}>
                {/* Date section header */}
                <div className="flex items-center gap-2 mb-3 sticky top-0 z-10 bg-[var(--bg-primary)]/90 backdrop-blur-sm py-2 -mx-3 sm:-mx-4 px-3 sm:px-4">
                  <div className="w-2 h-2 rounded-full bg-dyndev-green" />
                  <h3 className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
                    {group.label}
                  </h3>
                  <span className="text-[10px] text-[var(--text-secondary)]">
                    {group.entries.length} {group.entries.length === 1 ? 'entry' : 'entries'}
                  </span>
                </div>

                <div className="space-y-1">
                  {group.entries.map((entry: any) => {
                    const Icon = getActionIcon(entry.action);
                    const metaText = formatMetadata(entry.action, entry.metadata);
                    const isExpanded = expandedMetadata.has(entry.id);

                    return (
                      <div
                        key={entry.id}
                        className="relative flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg hover:bg-[var(--bg-secondary)]/50 transition-colors group"
                      >
                        {/* Timeline dot */}
                        <div
                          className={cn(
                            'relative z-10 w-[36px] h-[36px] sm:w-[46px] sm:h-[46px] rounded-full flex items-center justify-center flex-shrink-0',
                            getActionColor(entry.action),
                          )}
                        >
                          <Icon size={14} className="sm:size-[18px]" />
                        </div>

                        <div className="flex-1 min-w-0 pt-0.5 sm:pt-1">
                          <div className="flex items-start sm:items-center gap-1.5 sm:gap-2 mb-0.5 flex-col sm:flex-row sm:flex-wrap">
                            <span className="text-sm font-medium leading-tight">
                              {entry.user?.name || 'System'}
                            </span>
                            <span className="text-xs text-[var(--text-secondary)]">
                              {getActionLabel(entry.action)}
                            </span>
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-[var(--text-secondary)]">
                              {getEntityLabel(entry.entityType)}
                            </Badge>
                          </div>

                          <div className="flex items-center gap-2 text-[11px] text-[var(--text-secondary)] flex-wrap">
                            <Clock size={10} className="flex-shrink-0" />
                            <span>{formatDistanceToNow(entry.createdAt)}</span>
                            {entry.entityId && (
                              <>
                                <span className="text-[var(--border-color)]">·</span>
                                <span className="font-mono truncate max-w-[60px] sm:max-w-none">#{entry.entityId.slice(0, 8)}</span>
                              </>
                            )}
                            {/* IP address badge (admin only) */}
                            {isAdmin && entry.ipAddress && (
                              <>
                                <span className="text-[var(--border-color)]">·</span>
                                <span className="flex items-center gap-1 text-[10px]">
                                  <Globe size={9} />
                                  {entry.ipAddress}
                                </span>
                              </>
                            )}
                          </div>

                          {/* Metadata summary */}
                          {metaText && (
                            <p className="text-xs text-[var(--text-secondary)] mt-1 line-clamp-2 italic break-words">
                              {metaText}
                            </p>
                          )}

                          {/* Expandable raw metadata (admin only) */}
                          {isAdmin && entry.metadata && (
                            <div className="mt-1.5">
                              <button
                                onClick={() => toggleMetadata(entry.id)}
                                className="inline-flex items-center gap-1 text-[10px] text-dyndev-green hover:text-dyndev-green/80 transition-colors"
                              >
                                {isExpanded ? (
                                  <ChevronUp size={10} />
                                ) : (
                                  <ChevronDown size={10} />
                                )}
                                {isExpanded ? 'Hide metadata' : 'Show metadata'}
                              </button>
                              {isExpanded && (
                                <pre className="mt-1 p-2 rounded text-[10px] bg-[var(--bg-primary)] border border-[var(--border-color)] overflow-x-auto max-h-32 overflow-y-auto leading-relaxed">
                                  {JSON.stringify(entry.metadata, null, 2)}
                                </pre>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Load more */}
          {hasMore && (
            <div className="flex justify-center pt-4 pb-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleLoadMore}
                disabled={isFetching}
                className="gap-1.5"
              >
                {isFetching ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <ChevronDown size={14} />
                )}
                Load more
              </Button>
            </div>
          )}

          {/* Entry count */}
          {paginationInfo && (
            <p className="text-center text-[10px] text-[var(--text-secondary)] pt-2 pb-1">
              Showing {activity.length} of {totalEntries} entries
            </p>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
